import type { DriveStep, Side } from 'driver.js';
import type { HomeViewRole } from '@app/constants/home';
import { config as breakpointConfig } from '@app/hooks/useBreakpoints';
import { EDUCATOR_ROLE, STUDENT_ROLE } from '@core/constants/space';
import {
  HOME_TOUR_EDUCATOR_ORDER,
  HOME_TOUR_STEPS,
  HOME_TOUR_STUDENT_ORDER,
  HOME_TOUR_WELCOME,
  type HomeTourStepId,
} from './homeTourContent';
import { resolveHomeSpacesTourElement } from './resolveHomeSpacesTourElement';
import { resolveHomeRecentUpdatesTourElement } from './resolveHomeRecentUpdatesTourElement';

export type HomeTourStepDef = {
  id: string;
  element: string;
  title: string;
  description: string;
  side?: Side;
  narrowSide?: Side;
  align?: 'start' | 'center' | 'end';
};

/** Steps that target card internals — may mount after the parent widget. */
const DYNAMIC_TOUR_STEP_IDS = new Set<string>([
  'space-progress',
  'space-engagement',
  'space-cta',
  'recent-updates-reactions',
  'recent-updates-comments',
]);

export const resolveHomeTourPopoverSide = (
  side?: Side,
  narrowSide?: Side
): Side => {
  if (window.innerWidth < breakpointConfig.md) {
    return narrowSide ?? side ?? 'bottom';
  }
  return side ?? 'bottom';
};

export const getHomeTourWelcomeCopy = (
  role: HomeViewRole
): { title: string; description: string } => ({
  title: HOME_TOUR_WELCOME.title,
  description:
    role === EDUCATOR_ROLE
      ? HOME_TOUR_WELCOME.educator
      : HOME_TOUR_WELCOME.student,
});

const resolveStepDef = (
  stepId: HomeTourStepId,
  role: HomeViewRole
): HomeTourStepDef => {
  const step = HOME_TOUR_STEPS[stepId];
  const isEducator = role === EDUCATOR_ROLE;

  const title =
    (isEducator ? step.titleEducator : step.titleStudent) ?? step.title ?? '';

  const description =
    (isEducator ? step.descriptionEducator : step.descriptionStudent) ??
    step.description ??
    '';

  return {
    id: stepId,
    element: step.element,
    title,
    description,
    side: step.side,
    narrowSide: step.narrowSide,
    align: step.align,
  };
};

export const getHomeTourStepDefs = (role: HomeViewRole): HomeTourStepDef[] => {
  const order =
    role === EDUCATOR_ROLE ? HOME_TOUR_EDUCATOR_ORDER : HOME_TOUR_STUDENT_ORDER;

  return order.map((stepId) => resolveStepDef(stepId, role));
};

const isSpacesWidgetLoading = (root: ParentNode): boolean => {
  const widget = root.querySelector('#home-space-widget');
  return Boolean(widget?.querySelector('[aria-busy="true"]'));
};

const isRecentUpdatesWidgetLoading = (root: ParentNode): boolean => {
  const widget = root.querySelector('#home-recent-updates-widget');
  return Boolean(widget?.querySelector('[aria-busy="true"]'));
};

const widgetHasPosts = (root: ParentNode): boolean =>
  Boolean(
    root.querySelector(
      '#home-recent-updates-widget [data-tour-has-posts="true"]'
    ) || root.querySelector('[data-tour="home-recent-updates-card-cta"]')
  );

const widgetHasComments = (root: ParentNode): boolean =>
  Boolean(
    root.querySelector(
      '#home-recent-updates-widget [data-tour-has-comments="true"]'
    ) || root.querySelector('[data-tour="home-recent-updates-card-comments"]')
  );

const shouldIncludeTourStep = (
  step: HomeTourStepDef,
  root: ParentNode
): boolean => {
  // Space-card / recent-update action steps mount after the widget shell. Keep
  // them while cards are already present or the widget is still loading so an
  // early Start includes them. Exclude once the widget has settled empty/error —
  // otherwise skipMissingElement drops them at runtime but progress {{total}}
  // still counts them.
  if (step.id === 'recent-updates') {
    if (root.querySelector(step.element) && widgetHasPosts(root)) {
      return true;
    }
    return isRecentUpdatesWidgetLoading(root);
  }

  if (DYNAMIC_TOUR_STEP_IDS.has(step.id)) {
    if (root.querySelector(step.element)) {
      return true;
    }
    if (step.id === 'recent-updates-reactions') {
      return widgetHasPosts(root) || isRecentUpdatesWidgetLoading(root);
    }
    if (step.id === 'recent-updates-comments') {
      // Only count comments when a Common room post exists (or its CTA
      // is already mounted). Announcement-only feeds omit this step so
      // {{current}}/{{total}} stays accurate.
      return widgetHasComments(root);
    }
    return isSpacesWidgetLoading(root);
  }

  return Boolean(root.querySelector(step.element));
};

/**
 * Builds driver.js steps for the current home view role.
 * Targets missing from the DOM (hidden widgets, no role toggle, empty cards) are skipped.
 * The spaces step highlights the carousel strip without the greeting card.
 * Space-card steps are kept while spaces are loading so an early Start still
 * includes progress/engagement/CTA once cards mount; empty/error settles omit them.
 */
export const buildHomeTourSteps = (
  role: HomeViewRole = STUDENT_ROLE,
  root: ParentNode = document
): DriveStep[] => {
  return getHomeTourStepDefs(role)
    .filter((step) => shouldIncludeTourStep(step, root))
    .map((step) => {
      const isRecentUpdatesAction =
        step.id === 'recent-updates-reactions' ||
        step.id === 'recent-updates-comments';
      const element =
        step.id === 'spaces'
          ? () => resolveHomeSpacesTourElement(root) as Element
          : isRecentUpdatesAction
          ? () =>
              resolveHomeRecentUpdatesTourElement(step.element, root) as Element
          : DYNAMIC_TOUR_STEP_IDS.has(step.id)
          ? () => root.querySelector(step.element) as Element
          : step.element;

      return {
        element,
        data: { stepId: step.id, role },
        waitForElement: isRecentUpdatesAction ? 800 : undefined,
        popover: {
          title: step.title,
          description: step.description,
          side: resolveHomeTourPopoverSide(step.side, step.narrowSide),
          align: step.align ?? 'start',
        },
      };
    });
};
